/**
PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 *  Copyright © 2026 Pearson Education, Inc.
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
* */

/**
 * Hero Section V1 styles for Solution 1D
 *
 * @file Carousel.jsx
 * @author Ujjwal Sana
 * @since 1.2.8
 */

import React, { Fragment, useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import Slider from 'react-slick';
import PropTypes from 'prop-types';
import 'slick-carousel/slick/slick.scss';
import { Box } from '@mui/material';
import SlideArrowLeft from '../slidearrow/SlideArrowLeft';
import SlideArrowRight from '../slidearrow/SlideArrowRight';
import Utils from '../../common/utils';
import { CARD_BOUNDARY, PREV, NEXT } from '../../common/constants';

const Carousel = forwardRef(({
  children, enableSlider, enableArrowNav, gap, dots, onNavigation, disableArrows
}, ref) => {
  const sliderRef = useRef(null);
  const arrowRef = useRef(null);
  const [numOfCard, setNumOfCard] = useState(4);
  const [currentSlide, setCurrentSlide] = useState(0);

  /**
   * Method to handle the next event
   *
   */
  const handleNext = () => {
    onNavigation(NEXT);
    sliderRef.current.slickNext();
  };

  /**
   * Method to handle the previous event
   *
   */
  const handlePrev = () => {
    onNavigation(PREV);
    sliderRef.current.slickPrev();
  };

  const settings = {
    infinite: false,
    initialSlide: 0,
    variableWidth: true,
    arrows: !dots && enableArrowNav && !disableArrows,
    dots,
    // eslint-disable-next-line react/no-unstable-nested-components
    appendDots: () => (
      <Box ref={arrowRef}>
        <SlideArrowLeft
          onClick={currentSlide !== 0 ? handlePrev : null}
        />
        <SlideArrowRight
          onClick={(children && children.length - numOfCard) > currentSlide ? handleNext : null}
        />
      </Box>
    ),
    beforeChange: (current, next) => {
      setCurrentSlide(next);
    }
  };

  /**
   * Calculating and setting number of cards to be shown
   * number of cards to show is determined by Total container width + gap is divided by first element's width + gap
   *
   */
  const resizeHandler = Utils.debounce(() => {
    if (enableSlider && sliderRef.current) {
      const innnerSlider = sliderRef.current.innerSlider;

      if (innnerSlider
        && innnerSlider.list
        && innnerSlider.list.firstElementChild
        && innnerSlider.list.firstElementChild.firstElementChild) {
        const noOfcardsNumFraction = (innnerSlider.list.offsetWidth + gap)
        / (innnerSlider.list.firstElementChild.firstElementChild.offsetWidth + gap);
        const decimal = (noOfcardsNumFraction - Math.floor(noOfcardsNumFraction));
        const noOfCards = decimal >= CARD_BOUNDARY ? Math.ceil(noOfcardsNumFraction) : Math.floor(noOfcardsNumFraction);

        setNumOfCard(noOfCards);
      }
    }
  });

  /**
   * Post filter item click, resend to first page
   *
   * @param {*} index
   */
  const gotToSlide = (index) => {
    if (sliderRef.current) {
      setTimeout(() => {
        sliderRef.current.slickGoTo(index, true);
      }, 700);
    }
  };

  /**
   * Handle resize
   *
   */
  useEffect(() => {
    setTimeout(() => {
      resizeHandler();
    }, 100);
    window.addEventListener('resize', resizeHandler);

    return () => { window.removeEventListener('resize', resizeHandler); };
  }, []);

  useEffect(() => {
    resizeHandler();
  }, [children]);

  /**
   * To rearrange the dom elements
   *
   */
  useEffect(() => {
    try {
      if (sliderRef.current && arrowRef.current) {
        const parent = arrowRef.current.parentNode;
        parent.insertBefore(parent.lastChild, parent.firstChild);
      }
    } catch (error) {
      console.log(error);
    }
  }, []);

  /**
   * To set the tabindex manually
   *
   */
  useEffect(() => {
    try {
      setTimeout(() => {
        if (sliderRef.current
          && sliderRef.current.innerSlider
          && sliderRef.current.innerSlider.list
          && sliderRef.current.innerSlider.list.children[0]
          && sliderRef.current.innerSlider.list.children[0].children
          && Array.from(sliderRef.current.innerSlider.list.children[0].children).length > 0
        ) {
          sliderRef.current.innerSlider.list.setAttribute('role', 'list');
          Array.from(sliderRef.current.innerSlider.list.children[0].children).forEach((child) => {
            child.setAttribute('role', 'listitem');
            if (Array.from(child.classList).includes('slick-active')) {
              child.firstElementChild.firstElementChild.setAttribute('tabindex', '0');
            } else {
              child.firstElementChild.firstElementChild.setAttribute('tabindex', '-1');
            }
          });
        }
      }, 500);
    } catch (error) {
      console.log(error);
    }
  }, [currentSlide]);

  useImperativeHandle(ref, () => ({
    gotToSlide
  }));

  return (
    enableSlider
      ?
        <Slider
          ref={sliderRef}
          slidesToShow={numOfCard}
          slidesToScroll={numOfCard}
          swipe
          {...settings}
        >
          {children}
        </Slider>
      :
        <Fragment>
          {children}
        </Fragment>
  );
});


Carousel.defaultProps = {
  enableSlider: false,
  enableArrowNav: true,
  gap: 12,
  dots: false,
  onNavigation: () => {},
  disableArrows: false
};

Carousel.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]).isRequired,
  enableSlider: PropTypes.bool,
  enableArrowNav: PropTypes.bool,
  gap: PropTypes.number,
  dots: PropTypes.bool,
  onNavigation: PropTypes.func,
  disableArrows: PropTypes.bool
};


export default Carousel;
